import { getCommuneLabel } from 'shared/config/countries';

import type { MasterDetail, ProjectDetail } from '../../master/api/types';
import type { GlobalPromptSection, ProjectPromptSection } from '../api/promptSections.api';

export type SegmentKind =
  | 'plain'
  | 'date'
  | 'section'
  | 'globalSection'
  | 'masterProjectAttr'
  | 'subProjectAttr'
  | 'subProjects'
  | 'unresolved'
  | 'ifBlock'
  | 'ifEnd'
  | 'heading1'
  | 'heading2'
  | 'heading3';

/**
 * Renders one `<SUB_PROJECTS>` line, mirroring the backend `formatSubProjectLine`. The commune
 * segment is included only when the subproject has one, using the country-specific label.
 */
const formatSubProjectLine = (subProject: ProjectDetail, fallbackCountryCode: string | null | undefined): string => {
  const commune = subProject.commune?.trim() || '';
  const communePart = commune ? ` - ${getCommuneLabel(subProject.countryCode ?? fallbackCountryCode)}: ${commune}` : '';
  return `- ID: ${subProject.id} - Nombre: ${subProject.name}${communePart} - Descripción: ${subProject.description ?? ''}`;
};

export interface ResolvedSegment {
  kind: SegmentKind;
  text: string;
  token?: string;
  label?: string;
  severity?: 'warning' | 'error';
}

// Matches opening <IF:condition> and closing </IF:condition>
const TAG_PATTERN =
  /<(CURRENT_DATE|SUB_PROJECTS|SECTION:[^>]+|GLOBALSECTION:[^>]+|MASTERPROJECT:[^>]+|SUBPROJECT:[^>]+|IF:[^>]+|\/IF:[^>]+)>/gi;

// Used in postProcess to split plain text on heading lines
const HEADING_SPLIT_RE = /(^#{1,3} .+$)/m;
const HEADING_RE = /^(#{1,3}) (.+)$/;

function toStr(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

export interface CompileArgs {
  rawContent: string;
  globalSections: GlobalPromptSection[];
  projectSections: ProjectPromptSection[];
  masterProject: ProjectDetail | MasterDetail | null | undefined;
  subProject: ProjectDetail | null | undefined;
}

function postProcess(rawSegments: ResolvedSegment[]): ResolvedSegment[] {
  const result: ResolvedSegment[] = [];
  for (const seg of rawSegments) {
    if (seg.kind !== 'plain') {
      result.push(seg);
      continue;
    }
    const parts = seg.text.split(HEADING_SPLIT_RE);
    for (const part of parts) {
      if (!part) continue;
      const hMatch = part.match(HEADING_RE);
      if (hMatch) {
        const level = hMatch[1].length;
        const kind: SegmentKind = level === 1 ? 'heading1' : level === 2 ? 'heading2' : 'heading3';
        result.push({ kind, text: part }); // keep full line including # markers
      } else {
        result.push({ kind: 'plain', text: part });
      }
    }
  }
  return result;
}

export function compilePrompt({
  rawContent,
  globalSections,
  projectSections,
  masterProject,
  subProject,
}: CompileArgs): ResolvedSegment[] {
  const globalMap = new Map(globalSections.map((s) => [s.name.trim(), s]));
  const projectMap = new Map(projectSections.map((s) => [s.name.trim(), s]));
  const currentDate = new Date().toISOString().split('T')[0];

  const rawSegments: ResolvedSegment[] = [];
  const pattern = new RegExp(TAG_PATTERN.source, TAG_PATTERN.flags);
  let lastIndex = 0;
  let match = pattern.exec(rawContent);

  while (match !== null) {
    if (match.index > lastIndex) {
      rawSegments.push({ kind: 'plain', text: rawContent.slice(lastIndex, match.index) });
    }

    const token = match[0];
    const body = match[1];
    const upper = body.toUpperCase();

    if (upper === 'CURRENT_DATE') {
      rawSegments.push({ kind: 'date', text: currentDate, token, label: 'CURRENT_DATE' });
    } else if (upper === 'SUB_PROJECTS') {
      const subProjects = (masterProject as MasterDetail | null | undefined)?.subProjects ?? [];
      if (subProjects.length) {
        const text = subProjects.map((sub) => formatSubProjectLine(sub, masterProject?.countryCode)).join('\n');
        rawSegments.push({ kind: 'subProjects', text, token, label: 'SUB_PROJECTS' });
      } else {
        rawSegments.push({ kind: 'unresolved', text: token, token, label: 'SUB_PROJECTS', severity: 'warning' });
      }
    } else if (upper.startsWith('/IF:')) {
      rawSegments.push({ kind: 'ifEnd', text: token, token, label: body.slice('/IF:'.length).trim() });
    } else if (upper.startsWith('IF:')) {
      rawSegments.push({ kind: 'ifBlock', text: token, token, label: body.slice('IF:'.length).trim() });
    } else if (upper.startsWith('SECTION:')) {
      const name = body.slice('SECTION:'.length).trim();
      const section = projectMap.get(name);
      if (section) {
        rawSegments.push({ kind: 'section', text: section.value, token, label: name });
      } else {
        rawSegments.push({ kind: 'unresolved', text: token, token, label: name, severity: 'error' });
      }
    } else if (upper.startsWith('GLOBALSECTION:')) {
      const name = body.slice('GLOBALSECTION:'.length).trim();
      const section = globalMap.get(name);
      if (section) {
        rawSegments.push({ kind: 'globalSection', text: section.value, token, label: name });
      } else {
        rawSegments.push({ kind: 'unresolved', text: token, token, label: name, severity: 'error' });
      }
    } else if (upper.startsWith('MASTERPROJECT:')) {
      const attr = body.slice('MASTERPROJECT:'.length).trim();
      const val = masterProject ? toStr((masterProject as unknown as Record<string, unknown>)[attr]) : '';
      if (val) {
        rawSegments.push({ kind: 'masterProjectAttr', text: val, token, label: attr });
      } else {
        rawSegments.push({ kind: 'unresolved', text: token, token, label: attr, severity: 'warning' });
      }
    } else if (upper.startsWith('SUBPROJECT:')) {
      const attr = body.slice('SUBPROJECT:'.length).trim();
      const val = subProject ? toStr((subProject as unknown as Record<string, unknown>)[attr]) : '';
      if (val) {
        rawSegments.push({ kind: 'subProjectAttr', text: val, token, label: attr });
      } else {
        rawSegments.push({ kind: 'unresolved', text: token, token, label: attr, severity: 'warning' });
      }
    }

    lastIndex = match.index + token.length;
    match = pattern.exec(rawContent);
  }

  if (lastIndex < rawContent.length) {
    rawSegments.push({ kind: 'plain', text: rawContent.slice(lastIndex) });
  }
  if (rawSegments.length === 0) {
    rawSegments.push({ kind: 'plain', text: rawContent });
  }

  return postProcess(rawSegments);
}
